<template>
  <div id="app">
    <!-- 導覽列 -->
    <b-navbar type="dark" fixed="top" variant="info" class="navbar">
      <b-navbar-brand>花卉分級系統</b-navbar-brand>
      <b-collapse id="nav-text-collapse" is-nav>
        <b-navbar-nav>
          <b-nav-item :href="rootUrl">首頁</b-nav-item>
          <b-nav-item v-b-modal.model-standard>分級標準表</b-nav-item>
        </b-navbar-nav>
      </b-collapse>
    </b-navbar>
    <b-container class="flower_container">
      <b-alert show dismissible>
        您好，歡迎使用 🌻
        花卉分級系統，該系統可以幫助您辨識花卉等級，請上傳要辨識的花卉盆栽正面、側面圖片，接著填選花朵計算設定的盆栽尺寸、測定模型便可以測定結果，詳細分級規則可查看導覽列中的分級標準表。
      </b-alert>
      <b-row>
        <b-col cols="12" sm="6" lg="3">
          <BaseImageInput title="俯視圖" @loaded="addImage" />
        </b-col>
        <b-col cols="12" sm="6" lg="3">
          <BaseImageInput title="側視圖" @loaded="addSideImageLoaded" />
        </b-col>
        <b-col cols="12" sm="6" lg="3">
          <b-card header="花朵計算設定設定" class="mb-2">
            <b-form @submit.prevent="getCriticalResult">
              <b-form-group label="選擇尺寸">
                <b-form-select
                  v-model="form.potSize"
                  :options="potSize"
                  required
                />
              </b-form-group>
              <b-form-group label="選擇品種">
                <b-form-select
                  v-model="form.variety"
                  :options="variety"
                  required
                />
              </b-form-group>
              <b-form-group label="選擇模型">
                <b-form-select
                  v-model="form.model"
                  :options="yoloModelOptions"
                  required
                />
              </b-form-group>
              <b-form-checkbox v-model="form.roundnessThatkeepGreen">
                花面圓整度保留綠色
              </b-form-checkbox>
              <b-form-checkbox v-model="form.closenessThatkeepGreen">
                花序緊密度保留綠色
              </b-form-checkbox>
              <b-button
                block
                class="mt-3 mb-3"
                type="submit"
                variant="primary"
                :disabled="status.calculating || status.sendingResult"
              >
                計算
              </b-button>
            </b-form>
            <b-progress
              :value="processValue"
              show-progress
              animated
              v-if="processValue"
            />
          </b-card>
        </b-col>
        <!-- 花卉評分結果 -->
        <b-col cols="12" sm="6" lg="3">
          <b-card class="mb-2" header="花卉評分結果">
            <b-card-title>
              綜合結果: {{ calculationResults || '--' }}
              <span
                class="text-success d-none"
                :class="{ 'd-inline-block': status.resultModified }"
              >
                ( {{ formResultModify.correctResult }} )
              </span>
            </b-card-title>
            <b-list-group>
              <b-list-group-item>
                花序數量：{{
                  calculationResults ? criticalFormatResult.cyathia : '--'
                }}
                <span
                  class="text-success d-none"
                  :class="{ 'd-inline-block': status.resultModified }"
                >
                  ( {{ formResultModify.correctCyathia }} )
                </span>
              </b-list-group-item>
              <b-list-group-item>
                花面圓整度：{{
                  calculationResults
                    ? criticalFormatResult.roundness.level
                    : '--'
                }}
                <span
                  class="text-success d-none"
                  :class="{ 'd-inline-block': status.resultModified }"
                >
                  ( {{ formResultModify.correctRoundness }} )
                </span>
                <div v-if="criticalFormatResult">
                  <b-link
                    target="_blank"
                    v-for="(item, index) in criticalFormatResult.roundness.urls"
                    :href="item"
                    :key="index"
                  >
                    {{ '預覽' + (index + 1) }}
                  </b-link>
                </div>
              </b-list-group-item>
              <b-list-group-item>
                花序緊密度：{{
                  calculationResults
                    ? criticalFormatResult.closeness.level
                    : '--'
                }}
                <span
                  class="text-success d-none"
                  :class="{ 'd-inline-block': status.resultModified }"
                >
                  ( {{ formResultModify.correctCloseness }} )
                </span>
                <div v-if="criticalFormatResult">
                  <b-link
                    target="_blank"
                    v-for="(item, index) in criticalFormatResult.closeness.urls"
                    :href="item"
                    :key="index"
                  >
                    {{ '預覽' + (index + 1) }}
                  </b-link>
                </div>
              </b-list-group-item>
              <b-list-group-item>
                花莖整齊度：{{
                  calculationResults
                    ? criticalFormatResult.tidiness.level
                    : '--'
                }}
                <span
                  class="text-success d-none"
                  :class="{ 'd-inline-block': status.resultModified }"
                >
                  ( {{ formResultModify.correctTidiness }} )
                </span>
                <div v-if="criticalFormatResult">
                  <b-link
                    target="_blank"
                    v-for="(item, index) in criticalFormatResult.tidiness.urls"
                    :href="item"
                    :key="index"
                  >
                    {{ '預覽' + (index + 1) }}
                  </b-link>
                </div>
              </b-list-group-item>
              <b-list-group-item>
                株高：{{
                  calculationResults ? criticalFormatResult.size.height : '--'
                }}
                <span
                  class="text-success d-none"
                  :class="{ 'd-inline-block': status.resultModified }"
                >
                  ( {{ formResultModify.correctSizeHeight }} )
                </span>
                <br />
                株寬：{{
                  calculationResults ? criticalFormatResult.size.width : '--'
                }}
                <span
                  class="text-success d-none"
                  :class="{ 'd-inline-block': status.resultModified }"
                >
                  ( {{ formResultModify.correctSizeWidth }} )
                </span>
                <div v-if="criticalFormatResult">
                  <b-link
                    target="_blank"
                    v-for="(item, index) in criticalFormatResult.size.urls"
                    :href="item"
                    :key="index"
                  >
                    {{ '預覽' + (index + 1) }}
                  </b-link>
                </div>
              </b-list-group-item>
            </b-list-group>

            <b-form
              class="mt-3"
              v-show="status.resultCanBeSaved && !!access_token"
            >
              <b-button
                block
                class="mb-3"
                :disabled="status.sendingResult"
                v-b-modal.model-result-modify
                variant="success"
              >
                修改
              </b-button>
              <b-form-group label="批號">
                <b-form-input
                  maxlength="100"
                  v-model="formResult.batchNumber"
                  @keydown.enter.prevent="saveResult"
                ></b-form-input>
              </b-form-group>
              <b-button
                block
                class="mt-3 mb-3"
                @click="saveResult"
                :disabled="status.sendingResult"
                variant="primary"
              >
                <b-spinner small v-show="status.sendingResult"> </b-spinner>
                &ensp;{{ result.id ? '更新' : '儲存' }}&ensp;
              </b-button>
            </b-form>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
    <!-- 花卉分級表 v-model -->
    <b-modal
      ok-only
      size="xl"
      hide-footer
      ok-title="關閉"
      id="model-standard"
      :scrollable="true"
    >
      <template v-slot:modal-title> 花卉分級標準表 </template>
      <BaseTable v-for="item in standard" :standard="item" :key="item.name">{{
        item.name
      }}</BaseTable>
    </b-modal>
    <!-- 修改結果 -->
    <b-modal
      size="sm"
      ok-title="確定"
      cancel-title="取消"
      title="修改綜合結果"
      id="model-result-modify"
      :scrollable="true"
      @show="onShowResultModify"
      @ok="onOkResultModify"
    >
      <b-form>
        <b-form-group label="綜合結果">
          <b-form-input
            v-model="formResultModify.correctResult"
            type="text"
            plaintext
            disabled
          >
          </b-form-input>
        </b-form-group>

        <b-form-group label="花序數量">
          <b-form-input
            v-model="formResultModify.correctCyathia"
            type="number"
            min="0"
            @change="onChangeResultModify"
          >
          </b-form-input>
        </b-form-group>

        <b-form-group label="花面圓整度">
          <b-form-select
            v-model="formResultModify.correctRoundness"
            :options="optionsFlowerLevel"
            @change="onChangeResultModify"
          >
          </b-form-select>
        </b-form-group>

        <b-form-group label="花序緊密度">
          <b-form-select
            v-model="formResultModify.correctCloseness"
            :options="optionsFlowerLevel"
            @change="onChangeResultModify"
          >
          </b-form-select>
        </b-form-group>

        <b-form-group label="花莖整齊度">
          <b-form-select
            v-model="formResultModify.correctTidiness"
            :options="optionsFlowerLevel"
            @change="onChangeResultModify"
          >
          </b-form-select>
        </b-form-group>

        <b-form-group label="株高">
          <b-form-input
            v-model="formResultModify.correctSizeHeight"
            type="number"
            step="0.01"
            min="0"
            @change="onChangeResultModify"
          >
          </b-form-input>
        </b-form-group>

        <b-form-group label="株寬">
          <b-form-input
            v-model="formResultModify.correctSizeWidth"
            type="number"
            step="0.01"
            min="0"
            @change="onChangeResultModify"
          >
          </b-form-input>
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
/**
 * data.criticalResult：發出 api，得到的結果
 * data.criticalFormatResult：利用 criticalResult，整理出畫面的結果
 */

// plugin
import axios from 'axios';
import JSZip from 'jszip';
import mixin from './lib/mixin.js';

// json file
import standardTable from './assets/data/standardTable.json';

// component
import BaseTable from './components/Base/BaseTable';
import BaseImageInput from './components/Base/BaseImageInput';

const YOLO_HOST = 'https://ml.webduino.tw';

const instance = axios.create({
  headers: { 'Content-Type': 'multipart/form-data' },
});

export default {
  name: 'App',
  mixins: [mixin],
  components: {
    BaseTable,
    BaseImageInput,
  },
  data() {
    return {
      yoloModels: [],
      access_token: null, // 呼叫桃改專家系統
      rootUrl: '/',
      processValue: 0,
      potSize: [
        {
          value: null,
          text: '請選擇尺寸分級表',
        },
        {
          value: {
            id: 3,
            text: 'threeInches',
          },
          text: '三寸分級表',
        },
        {
          value: {
            id: 5,
            text: 'fiveInches',
          },
          text: '五寸分級表',
        },
      ],
      variety: [
        {
          value: null,
          text: '請選擇品種',
        },
        {
          value: 'general',
          text: '一般品種',
        },
        {
          value: 'princess',
          text: '公主品種',
        },
      ],
      // 等級選單
      optionsResultLevel: [
        {
          value: 'A+',
          text: 'A+',
        },
        {
          value: 'A',
          text: 'A',
        },
        {
          value: 'B',
          text: 'B',
        },
        {
          value: 'C',
          text: 'C',
        },
      ],
      // 等級選單，包含花面圓整度、花序緊密度、花莖整齊度
      optionsFlowerLevel: [
        {
          value: 'A',
          text: 'A',
        },
        {
          value: 'B',
          text: 'B',
        },
        {
          value: 'C',
          text: 'C',
        },
      ],
      // 花朵計算設定
      form: {
        model: 8, // 選擇模型
        potSize: null, // 選擇尺寸
        variety: null, // 選擇品種
        sideImages: [], // 側視圖
        positiveImages: [], // 俯視圖
        roundnessThatkeepGreen: false, // 花面圓整度保留綠色
        closenessThatkeepGreen: false, // 花面緊密度保留綠色
      },
      // 花卉評分結果，儲存功能
      formResult: {
        batchNumber: '',
      },
      // 花卉評分結果，修改
      formResultModify: {
        correctResult: '',
        correctCyathia: '',
        correctRoundness: '',
        correctCloseness: '',
        correctTidiness: '',
        correctSizeHeight: '',
        correctSizeWidth: '',
      },
      // api 請求回計算資料
      criticalResult: [],
      // 送到 server 儲存的資料
      result: {
        id: null, // 儲存的 table id
        pictureLink: null, // 下載圖片的路徑
        zipfile: null,
        batchNumber: '',
        potSize: '',
        variety: '',
        models: '',
        roundnessGreen: '',
        closenessGreen: '',
        result: '',
        cyathia: '',
        roundness: '',
        closeness: '',
        tidiness: '',
        sizeHeight: '',
        sizeWidth: '',
        // 修正資料不塞預設值
        // correctResult: "",
        // correctCyathia: "",
        // correctRoundness: "",
        // correctCloseness: "",
        // correctTidiness: "",
        // correctSizeHeight: "",
        // correctSizeWidth: "",
      },
      // 狀態的控制
      status: {
        resultCanBeSaved: false, // 可執行儲存
        sendingResult: false, // 發送儲存指令中
        calculating: false, // 計算結果中
        resultModified: false, // 綜合結果是否修改過
      },
    };
  },
  methods: {
    async getYoloModels() {
      try {
        this.status.isResponseError = false;
        const { data: result } = await axios.get(
          `${YOLO_HOST}/yolo/detectors/`,
          {
            params: {
              hashkey: this.getHash(),
            },
          }
        );
        return result;
      } catch (error) {
        this.status.isResponseError = true;
        console.error(error);
        return null;
      }
    },
    // 新增正面圖片
    addImage(files) {
      this.form.positiveImages.length = 0;
      this.form.positiveImages.push(...files);
    },
    // 新增側面圖片
    addSideImageLoaded(files) {
      this.form.sideImages.length = 0;
      this.form.sideImages.push(...files);
    },
    // 計算，根據畫面的設定，打後端的 api，得到原始的資料。
    // 原始的資料，需要再經過運算，才會得到畫面顯示的最終結果。
    async getCriticalResult() {
      this.resetCriticalResult();
      this.status.calculating = true;
      this.status.resultCanBeSaved = false;
      this.status.resultModified = false;

      // 恢復儲存資料的預設值
      this.$data.result = this.$options.data().result;

      if (!this.form.positiveImages.length || !this.form.sideImages.length)
        return;

      try {
        this.processValue = 10; // 進度條

        // 物件辨識需要依序進行
        const flowerCounts = (() => {
          return new Promise(async (resolve) => {
            const returnResults = [];
            for (const image of this.form.positiveImages) {
              const { data } = await this.getFlowerCountByYolo(image);
              returnResults.push(data);
            }
            this.processValue = this.processValue + 40;
            resolve(returnResults);
          });
        })();

        const flowerRoundnessAndtidiness = Promise.all(
          this.form.positiveImages.map((image) =>
            this.getFlowerRoundnessAndtidiness(image)
          )
        ).then((responses) => {
          this.processValue = this.processValue + 20;
          return responses.map((response) => response.data);
        });

        const flowerWidthAndHeightAndCloseness = Promise.all(
          this.form.sideImages.map((image) =>
            this.getFlowerWidthAndHeightAndCloseness(image)
          )
        ).then((responses) => {
          this.processValue = this.processValue + 20;
          return responses.map((response) => response.data);
        });

        this.criticalResult = await Promise.all([
          flowerCounts,
          flowerRoundnessAndtidiness,
          flowerWidthAndHeightAndCloseness,
        ]);

        // 可以儲存結果
        this.status.resultCanBeSaved = true;
      } catch (error) {
        console.error(error);
      } finally {
        this.processValue = 100;
        setTimeout(() => {
          this.processValue = 0; // 進度條歸零
          this.status.calculating = false;
        }, 300);
      }
    },
    /**
     * 取得花序辨識資訊
     * returns {object} { label, url }
     */
    getFlowerCountByYolo(image) {
      const form = new FormData();
      form.append('image', image);
      return axios.put(
        `${YOLO_HOST}/yolo/detectors/${this.form.model}/identify`,
        form,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: {
            hashkey: this.getHash(),
          },
        }
      );
    },
    /**
     * 取得花朵緊密度及圓整度
     * returns {object} { closeness, roundness }
     */
    getFlowerRoundnessAndtidiness(image) {
      const form = new FormData();
      form.append('file', image);
      return instance.post(
        `https://taoyuan-plant-score.webduino.tw/top/removegreen`,
        form,
        {
          params: {
            roundness: !this.form.roundnessThatkeepGreen,
            closeness: !this.form.closenessThatkeepGreen,
            size_type: this.form.potSize.id,
          },
        }
      );
    },
    /**
     * 取得株高、株寬、花莖整齊度
     * returns {object} { size, tidiness }
     */
    getFlowerWidthAndHeightAndCloseness(image) {
      const form = new FormData();
      form.append('file', image);
      return instance.post(
        `https://taoyuan-plant-score.webduino.tw/side`,
        form,
        {
          params: {
            size_type: this.form.potSize.id,
          },
        }
      );
    },
    // 取得 standard 中的五寸、三寸評分資料
    formatStandard(standard, potSize) {
      return standard[potSize].data.reduce((accumulator, currentValue) => {
        accumulator[currentValue.level] = {
          count: {
            max: currentValue.count[this.form.variety].max,
            min: currentValue.count[this.form.variety].min,
          },
          roundness: currentValue.roundness,
          closeness: currentValue.closeness,
          tidiness: currentValue.tidiness,
        };
        return accumulator;
      }, {});
    },
    // 計算花朵數量成為 A+、A、B、C 等級
    countFlowerLevel(count, standard) {
      const { 'A+': Aplus, A, B, C } = standard;
      if (count <= Aplus.count.max && count >= Aplus.count.min) {
        return 'A+';
      } else if (count <= A.count.max && count >= A.count.min) {
        return 'A';
      } else if (count <= B.count.max && count >= B.count.min) {
        return 'B';
      } else {
        return 'C';
      }
    },
    // 計算三寸花盆分數
    countThreeInchesScore(score) {
      const { count, roundness, closeness, tidiness } = score;
      const threeScores = [roundness, closeness, tidiness];
      if (count === 'A+' && threeScores.every((item) => item === 'A')) {
        return 'A+';
      } else if (
        ['A+', 'A'].some((item) => item === count) &&
        threeScores.every((item) => item === 'A')
      ) {
        return 'A';
      } else if (
        ['A+', 'A', 'B'].some((item) => item === count) &&
        threeScores.every((item) => item !== 'C')
      ) {
        return 'B';
      } else {
        return 'C';
      }
    },
    // 計算五寸花盆分數
    countFiveInchesScore(score) {
      const { count, roundness, closeness, tidiness } = score;
      const threeScores = [roundness, closeness, tidiness];
      if (count === 'A+' && threeScores.every((item) => item === 'A')) {
        return 'A+';
      } else if (
        ['A+', 'A'].some((item) => item === count) &&
        threeScores.every((item) => item === 'A')
      ) {
        return 'A';
      } else if (
        ['A+', 'A', 'B'].some((item) => item === count) &&
        threeScores.every((item) => item !== 'C')
      ) {
        return 'B';
      } else {
        return 'C';
      }
    },
    // 讀取 text 文字
    readText(text) {
      const speechContent = text
        ? `花卉分級系統綜合評分為 ${text} 級。`
        : '花卉分級系統無法判定。';
      const speechInstance = new SpeechSynthesisUtterance(speechContent);

      speechInstance.lang = 'zh-tw';
      speechInstance.pitch = 0.8;
      speechInstance.rate = 0.8;
      window.speechSynthesis.speak(speechInstance);
    },
    resetCriticalResult() {
      this.criticalResult.length = 0;
    },
    /**
     * 儲存結果
     */
    async saveResult() {
      if (!this.access_token) return;

      const getModels = (modelIds) => {
        const models = this.yoloModelOptions.filter((model) =>
          modelIds.includes(model.value)
        );
        return models.map((model) => ({
          id: model.value,
          name: model.text,
          type: 'yolo', // 物件辨識模型
        }));
      };

      this.status.sendingResult = true;

      let zipfile = new JSZip();

      // 俯視圖
      this.form.positiveImages.forEach((image) => {
        zipfile.file(image.name || Date.now(), image, { base64: true });
      });

      // 側視圖
      this.form.sideImages.forEach((image) => {
        zipfile.file(image.name || Date.now(), image, { base64: true });
      });

      // 花面圓整度
      for (const index in this.criticalFormatResult.roundness.urls) {
        const url = this.criticalFormatResult.roundness.urls[index];
        const [, name, imageType] = /.*\/(.+)\.(.+)/.exec(url);
        const image = await this.getFileFromUrl(
          url,
          `${name}-${index}.${imageType}`,
          `image/${imageType}`
        );
        zipfile.file(image.name, image, { base64: true });
      }

      // 花序緊密度
      for (const index in this.criticalFormatResult.closeness.urls) {
        const url = this.criticalFormatResult.closeness.urls[index];
        const [, name, imageType] = /.*\/(.+)\.(.+)/.exec(url);
        const image = await this.getFileFromUrl(
          url,
          `${name}-${index}.${imageType}`,
          `image/${imageType}`
        );
        zipfile.file(image.name, image, { base64: true });
      }

      // 花莖整齊度
      for (const index in this.criticalFormatResult.tidiness.urls) {
        const url = this.criticalFormatResult.tidiness.urls[index];
        const [, name, imageType] = /.*\/(.+)\.(.+)/.exec(url);
        const image = await this.getFileFromUrl(
          url,
          `${name}-${index}.${imageType}`,
          `image/${imageType}`
        );
        zipfile.file(image.name, image, { base64: true });
      }

      // 株高/株寬
      for (const index in this.criticalFormatResult.size.urls) {
        const url = this.criticalFormatResult.size.urls[index];
        const [, name, imageType] = /.*\/(.+)\.(.+)/.exec(url);
        const image = await this.getFileFromUrl(
          url,
          `${name}-${index}.${imageType}`,
          `image/${imageType}`
        );
        zipfile.file(image.name, image, { base64: true });
      }

      // blob to file
      // https://stackoverflow.com/questions/27159179/how-to-convert-blob-to-file-in-javascript
      // zipfile.lastModifiedDate = new Date();
      // zipfile.name = `${Date.now().zip}`;
      zipfile = await zipfile.generateAsync({ type: 'blob' });
      zipfile = new File([zipfile], `${Date.now().zip}`);

      Object.assign(this.result, {
        zipfile, // 當 this.result.pictureLink 有值時，這個值是無效的
        batchNumber: this.formResult.batchNumber,
        potSize: this.form.potSize.id,
        variety: this.form.variety,
        models: JSON.stringify(getModels([this.form.model])),
        roundnessGreen: this.form.roundnessThatkeepGreen,
        closenessGreen: this.form.closenessThatkeepGreen,
        result: this.calculationResults,
        cyathia: this.criticalFormatResult.cyathia,
        roundness: this.criticalFormatResult.roundness.level,
        closeness: this.criticalFormatResult.closeness.level,
        tidiness: this.criticalFormatResult.tidiness.level,
        sizeHeight: parseFloat(this.criticalFormatResult.size.height),
        sizeWidth: parseFloat(this.criticalFormatResult.size.width),
      });

      if (this.status.resultModified) {
        Object.assign(this.result, this.formResultModify);
      }

      const form = new FormData();
      for (const key in this.result) {
        const value = this.result[key];
        if (value !== null && value !== undefined) form.append(key, value);
      }

      try {
        const {
          data: { instance: result },
        } = await axios.put(
          `${this.taoyuanExpertHost()}/api/flower_poinsettia/uploadResult`,
          form,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: this.access_token,
            },
          }
        );

        // 結果中有 id 時，就會更新該筆資料
        this.result.id = result.id;
        this.result.pictureLink = result.pictureLink;
      } catch (error) {
        console.error(error);
      } finally {
        this.status.sendingResult = false;
      }
    },
    // 同步資料到修改結果的 form
    syncToFormResultModify() {
      this.formResultModify.correctResult = this.calculationResults;
      this.formResultModify.correctCyathia = this.criticalFormatResult.cyathia;
      this.formResultModify.correctRoundness =
        this.criticalFormatResult.roundness.level;
      this.formResultModify.correctCloseness =
        this.criticalFormatResult.closeness.level;
      this.formResultModify.correctTidiness =
        this.criticalFormatResult.tidiness.level;
      this.formResultModify.correctSizeHeight = parseFloat(
        this.criticalFormatResult.size.height
      );
      this.formResultModify.correctSizeWidth = parseFloat(
        this.criticalFormatResult.size.width
      );
    },
    onShowResultModify() {
      if (this.status.resultModified) return;
      this.syncToFormResultModify();
    },
    onOkResultModify() {
      this.status.resultModified = true;
    },
    onChangeResultModify() {
      const score = {
        count: null,
        tidiness: this.formResultModify.correctTidiness,
        roundness: this.formResultModify.correctRoundness,
        closeness: this.formResultModify.correctCloseness,
      };
      const standard = this.formatStandard(
        this.standard,
        this.form.potSize.text
      );
      // 判斷計算結果，先算出花朵級數
      score.count = this.countFlowerLevel(
        this.formResultModify.correctCyathia,
        standard
      );
      // 計算總判斷結果
      if (this.form.potSize.text === 'threeInches') {
        this.formResultModify.correctResult = this.countThreeInchesScore(score);
      }
      if (this.form.potSize.text === 'fiveInches') {
        this.formResultModify.correctResult = this.countFiveInchesScore(score);
      }
    },
  },
  watch: {
    calculationResults: {
      handler(calculationResults) {
        if (!calculationResults) return;
        return this.readText(calculationResults);
      },
    },
    form: {
      handler() {
        // 不可執行儲存
        this.status.resultCanBeSaved = false;
      },
      deep: true,
    },
  },
  computed: {
    standard() {
      return standardTable;
    },
    // 將 API 結果 ( this.criticalResult ) 轉換成自己需要的格式圖片 url 集中放在一起，數值，依規則做運算
    criticalFormatResult() {
      if (!this.criticalResult.length) return;

      let [data] = this.criticalResult;
      // 花序數量 (取最大值)
      const cyathia = ((data) => {
        const cyathiaAry = data.map((val) => {
          const cyathia = val.label.cyathia ? val.label.cyathia.length : 0;
          const cyathium = val.label.cyathium ? val.label.cyathium.length : 0;
          return cyathia > cyathium ? cyathia : cyathium;
        });
        return Math.max(...cyathiaAry);
      })(data);

      [, data] = this.criticalResult;
      // 花序緊密度 (取最大值)
      const closeness = ((data) => {
        const closenessAry = data.map((val) => val.closeness);
        const level = closenessAry.map((val) => val.level).sort()[0];
        const urls = closenessAry.map((val) => val.url);
        return {
          level,
          urls,
        };
      })(data);

      // 花面圓整度 (取最大值)
      const roundness = ((data) => {
        const roundnessAry = data.map((val) => val.roundness);
        const level = roundnessAry.map((val) => val.level).sort()[0];
        const urls = roundnessAry.map((val) => val.url);
        return {
          level,
          urls,
        };
      })(data);

      [, , data] = this.criticalResult;
      // 株高/株寬 (取平均值)
      const size = ((data) => {
        const sizeAry = data.map((val) => val.size);
        let width = sizeAry.reduce((acc, val) => acc + val.width, 0);
        width = (width / sizeAry.length).toFixed(2);
        let height = sizeAry.reduce((acc, val) => acc + val.height, 0);
        height = (height / sizeAry.length).toFixed(2);
        let urls = sizeAry.map((val) => val.url);
        return {
          width,
          height,
          urls,
        };
      })(data);

      // 花莖整齊度 (取平均值)
      // TODO: 這裡的計算有問題，之後再處理
      const tidiness = ((data) => {
        const tidinessAry = data.map((val) => val.tidiness);
        let level = tidinessAry.map((val) => val.level).sort();
        level = level[1] || level[0]; // 當作取平均值的方式，若有 3 組結果，取中間的，若有 2 組，則取最後 1 組。
        const urls = tidinessAry.map((val) => val.url);
        return {
          level,
          urls,
        };
      })(data);

      return {
        size,
        cyathia,
        tidiness,
        closeness,
        roundness,
      };
    },
    // 計算最終分級
    calculationResults() {
      if (!this.criticalFormatResult) return;
      // 取得判斷的空結果
      const score = {
        count: null,
        tidiness: this.criticalFormatResult.tidiness.level,
        roundness: this.criticalFormatResult.roundness.level,
        closeness: this.criticalFormatResult.closeness.level,
      };
      const standard = this.formatStandard(
        this.standard,
        this.form.potSize.text
      );
      // 判斷計算結果，先算出花朵級數
      score.count = this.countFlowerLevel(
        this.criticalFormatResult.cyathia,
        standard
      );
      // 計算總判斷結果
      if (this.form.potSize.text === 'threeInches')
        return this.countThreeInchesScore(score);
      if (this.form.potSize.text === 'fiveInches')
        return this.countFiveInchesScore(score);
    },
    yoloModelOptions() {
      return this.yoloModels.map((model) => ({
        value: model.id,
        text: model.name,
      }));
    },
  },
  async created() {
    const yoloModels = await this.getYoloModels();
    this.yoloModels.push(...yoloModels);
    this.access_token = this.getAccessToken();
    this.rootUrl = this.rootUrl + location.search;
  },
};
</script>

<style lang="scss" scoped>
.flower_container {
  margin-top: 80px;
}
</style>
